import React, { useEffect, useMemo, useState } from "react";
import { useParams, NavLink } from "react-router-dom";
import { supabase } from "../supabaseClient";
import {
  CalendarClock,
  Trophy,
  Dumbbell,
  Filter,
  MapPin,
  Clock,
  Loader2,
  AlertTriangle,
} from "lucide-react";

// ─── Types ───────────────────────────────────────────────────────────────────

type EventType = "match" | "training";

type TeamEvent = {
  id: string;
  type: EventType;
  startAt: string;
  endAt?: string;
  location?: string;
  opponent?: string;
  notes?: string;
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function cx(...classes: Array<string | false | undefined | null>) {
  return classes.filter(Boolean).join(" ");
}

function sameDay(a: Date, b: Date) {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function formatDayLabel(d: Date) {
  return d.toLocaleDateString("fr-FR", { weekday: "long", day: "2-digit", month: "long" }).replace(/^\w/, (c) => c.toUpperCase());
}

function formatTime(iso: string) {
  return new Date(iso).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
}

// ─── Main ─────────────────────────────────────────────────────────────────────

export default function TeamEvents() {
  const { clubId, teamId } = useParams<{ clubId: string; teamId: string }>();

  const [events, setEvents] = useState<TeamEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [typeFilter, setTypeFilter] = useState<"all" | EventType>("all");
  const [periodFilter, setPeriodFilter] = useState<"upcoming" | "past" | "all">("upcoming");

  useEffect(() => {
    if (!teamId || !clubId) return;
    let cancelled = false;

    async function load() {
      try {
        setLoading(true);
        setError("");

        const [matchesRes, trainingsRes] = await Promise.all([
          supabase
            .from("matches")
            .select("id, date, opponent, location")
            .eq("team_id", teamId)
            .order("date", { ascending: true }),
          supabase
            .from("trainings")
            .select("id, date, location, notes, end_time")
            .eq("team_id", teamId)
            .order("date", { ascending: true }),
        ]);

        if (matchesRes.error) throw matchesRes.error;
        if (trainingsRes.error) throw trainingsRes.error;

        if (!cancelled) {
          const matchEvents: TeamEvent[] = (matchesRes.data || []).map((m: any) => ({
            id: m.id,
            type: "match" as const,
            startAt: m.date,
            opponent: m.opponent ?? undefined,
            location: m.location ?? undefined,
          }));

          const trainingEvents: TeamEvent[] = (trainingsRes.data || []).map((t: any) => ({
            id: t.id,
            type: "training" as const,
            startAt: t.date,
            endAt: t.end_time ?? undefined,
            location: t.location ?? undefined,
            notes: t.notes ?? undefined,
          }));

          setEvents(
            [...matchEvents, ...trainingEvents].sort(
              (a, b) => new Date(a.startAt).getTime() - new Date(b.startAt).getTime()
            )
          );
        }
      } catch (err: any) {
        if (!cancelled) setError(err?.message || "Impossible de charger les événements.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => { cancelled = true; };
  }, [teamId, clubId]);

  const filteredEvents = useMemo(() => {
    let list = typeFilter === "all" ? events : events.filter((e) => e.type === typeFilter);

    const now = Date.now();
    if (periodFilter === "upcoming") {
      list = list
        .filter((e) => new Date(e.startAt).getTime() >= now)
        .sort((a, b) => new Date(a.startAt).getTime() - new Date(b.startAt).getTime());
    } else if (periodFilter === "past") {
      list = list
        .filter((e) => new Date(e.startAt).getTime() < now)
        .sort((a, b) => new Date(b.startAt).getTime() - new Date(a.startAt).getTime());
    } else {
      list = [...list].sort((a, b) => new Date(a.startAt).getTime() - new Date(b.startAt).getTime());
    }

    return list;
  }, [events, typeFilter, periodFilter]);

  const eventsByDay = useMemo(() => {
    const map = new Map<string, TeamEvent[]>();
    for (const e of filteredEvents) {
      const d = new Date(e.startAt);
      const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(e);
    }
    return map;
  }, [filteredEvents]);

  if (loading) {
    return (
      <div className="rounded-2xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 p-6">
        <div className="inline-flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
          <Loader2 className="h-4 w-4 animate-spin" />
          Chargement des événements…
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-2xl border border-red-200 bg-red-50 dark:border-red-900/40 dark:bg-red-950/30 p-4 text-sm text-red-700 dark:text-red-300">
        <div className="inline-flex items-center gap-2">
          <AlertTriangle className="h-4 w-4" />
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="text-center sm:text-left">
        <div className="text-sm text-gray-500 dark:text-gray-400">Équipe</div>
        <div className="text-xl font-semibold text-gray-900 dark:text-gray-100 flex items-center justify-center gap-2 sm:justify-start">
          <CalendarClock className="h-5 w-5 text-gray-500" />
          {periodFilter === "upcoming" ? "Événements à venir" : periodFilter === "past" ? "Événements passés" : "Tous les événements"}
        </div>
        <div className="text-sm text-gray-500 dark:text-gray-400">
          {filteredEvents.length} événement{filteredEvents.length !== 1 ? "s" : ""}.
        </div>
      </div>

      {/* Filters */}
      <div className="rounded-2xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 p-4 space-y-3">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="inline-flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200">
            <Filter className="h-4 w-4 text-gray-500" />
            Période
          </div>
          <div className="flex flex-wrap gap-2">
            {(["upcoming", "past", "all"] as const).map((f) => (
              <button
                key={f}
                type="button"
                onClick={() => setPeriodFilter(f)}
                className={cx(
                  "px-3 py-2 rounded-xl text-sm border",
                  periodFilter === f
                    ? "bg-gray-100 text-gray-900 border-gray-200 dark:bg-gray-800 dark:text-gray-100 dark:border-gray-700"
                    : "bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-200 border-gray-200 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800"
                )}
              >
                {f === "upcoming" ? "À venir" : f === "past" ? "Passés" : "Tous"}
              </button>
            ))}
          </div>
        </div>

        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between pt-3 border-t border-gray-100 dark:border-gray-800">
          <div className="inline-flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200">
            <Filter className="h-4 w-4 text-gray-500" />
            Type
          </div>
          <div className="flex flex-wrap gap-2">
            {(["all", "match", "training"] as const).map((f) => (
              <button
                key={f}
                type="button"
                onClick={() => setTypeFilter(f)}
                className={cx(
                  "px-3 py-2 rounded-xl text-sm border",
                  typeFilter === f
                    ? "bg-gray-100 text-gray-900 border-gray-200 dark:bg-gray-800 dark:text-gray-100 dark:border-gray-700"
                    : "bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-200 border-gray-200 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800"
                )}
              >
                {f === "all" ? "Tout" : f === "match" ? "Matchs" : "Entraînements"}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* List */}
      {filteredEvents.length === 0 ? (
        <div className="rounded-2xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 p-10 flex flex-col items-center gap-2 text-gray-400 dark:text-gray-500">
          <CalendarClock className="h-8 w-8" />
          <div className="text-sm">Aucun événement pour ce filtre.</div>
        </div>
      ) : (
        <div className="space-y-4">
          {Array.from(eventsByDay.entries()).map(([key, dayEvents]) => {
            const [y, m, d] = key.split("-").map(Number);
            const day = new Date(y, m, d);
            const isToday = sameDay(day, new Date());

            return (
              <div key={key} className="space-y-2">
                <div className={cx(
                  "text-sm font-semibold",
                  isToday ? "text-indigo-700 dark:text-indigo-300" : "text-gray-900 dark:text-gray-100"
                )}>
                  {formatDayLabel(day)}{isToday && " — Aujourd'hui"}
                </div>

                {dayEvents.map((ev) => {
                  const to = ev.type === "training"
                    ? `/club/${clubId}/team/${teamId}/calendar/${ev.id}`
                    : `/club/${clubId}/team/${teamId}/calendar/match/${ev.id}`;

                  return (
                    <NavLink key={ev.id} to={to} className="block">
                      <div className="rounded-2xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 p-4 hover:border-gray-300 dark:hover:border-gray-700 transition">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className={cx(
                            "inline-flex items-center gap-1.5 text-xs px-2 py-0.5 rounded-full",
                            ev.type === "match"
                              ? "bg-blue-100 text-blue-900 dark:bg-blue-900/20 dark:text-blue-200"
                              : "bg-green-100 text-green-900 dark:bg-green-900/20 dark:text-green-200"
                          )}>
                            {ev.type === "match"
                              ? <><Trophy className="h-3.5 w-3.5" />Match</>
                              : <><Dumbbell className="h-3.5 w-3.5" />Entraînement</>}
                          </span>

                          <div className="font-semibold text-gray-900 dark:text-gray-100 truncate">
                            {ev.type === "match" && ev.opponent ? `vs ${ev.opponent}` : ev.type === "match" ? "Match" : "Entraînement"}
                          </div>
                        </div>

                        <div className="mt-2 flex flex-wrap gap-3 text-xs text-gray-500 dark:text-gray-400">
                          <span className="inline-flex items-center gap-1">
                            <Clock className="h-3.5 w-3.5" />
                            {formatTime(ev.startAt)}
                            {ev.endAt ? ` → ${formatTime(ev.endAt)}` : ""}
                          </span>
                          {ev.location && (
                            <span className="inline-flex items-center gap-1">
                              <MapPin className="h-3.5 w-3.5" />
                              {ev.location}
                            </span>
                          )}
                        </div>

                        {ev.notes && (
                          <div className="mt-2 text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                            {ev.notes}
                          </div>
                        )}
                      </div>
                    </NavLink>
                  );
                })}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
